import axios from 'axios';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Office } from '../../../models/office';
import Title from '../../../components/Title';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useAxiosInterceptor from '../../AxiosInterceptor';
import {
  Box,
  Card,
  CardActions,
  Grid,
  Button,
  Divider,
  Typography,
  Checkbox,
  CardContent,
  Alert,
} from '@mui/material';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import { SimpleColumn } from '../../../components/table/interface';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
  stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';

const UserOffice = () => {
  useAxiosInterceptor();
  const navigate = useNavigate();
  const { drawerOpen } = useDrawer();
  const [selectedOffice, setSelectedOffice] = useState<number[]>([]);
  const [unselectedOffice, setUnselectedOffice] = useState<Office[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const { id } = useParams();
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const { data } = await axios.get('offices');
        setOffices(data.data);
      } catch (err) {
        console.error('Error fetching offices: ', err);
      }
    };

    fetchOffices();
  }, []);

  useEffect(() => {
    const fetchUserOffices = async () => {
      try {
        const { data } = await axios.get(`useroffice/${id}`);
        setSelectedOffice(data.users.map((userOffice: { office_id: number }) => userOffice.office_id));
      } catch (err) {
        console.error('Error fetching user offices: ', err);
      }
    };

    fetchUserOffices();
  }, [id]);

  const handleCheckboxChange = (officeId: number, isActive: boolean) => {
    if (isActive) {
      setSelectedOffice((prevSelected: number[]) => [...prevSelected, officeId]);
      setUnselectedOffice((prevUnselected: Office[]) => prevUnselected.filter((item) => item.id !== officeId));
    } else {
      setSelectedOffice((prevSelected: number[]) => prevSelected.filter((oid) => oid !== officeId));
      setUnselectedOffice((prevUnselected: Office[]) => {
        if (!prevUnselected.map((item) => item.id || item).includes(officeId)) {
          return [...prevUnselected, { id: officeId } as Office];
        }
        return prevUnselected;
      });
    }
  };

  const handleSelectAll = (isActive: boolean) => {
    if (isActive) {
      const allOfficeIds = offices.map((office) => office.id);
      setSelectedOffice(allOfficeIds);
      setUnselectedOffice([]);
    } else {
      setSelectedOffice([]);
      setUnselectedOffice(offices);
    }
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const setUnselectedOffices = unselectedOffice.map((office) => office.id);
    const formData = {
      officeId: selectedOffice,
      setUnselectedOffice: setUnselectedOffices,
      userId: id,
    };
    try {
      await axios.post('/useroffice', formData);
      navigate('/administrar/usuarios');
      setSuccess('Registros realizados exitosamente');
      setShowSuccess(true);
    } catch (err) {
      console.error('Error:', err);
      setShowError(true);
    }
  };

  const columns: SimpleColumn[] = [
    { field: 'name', headerName: 'Nombre', minWidth: 240, wrapText: true },
    {
      field: 'options',
      headerName: 'Seleccionar',
      minWidth: 200,
      align: 'center',
      renderHeader: () => (
        <Checkbox
          indeterminate={selectedOffice.length > 0 && selectedOffice.length < offices.length}
          checked={offices.length > 0 && selectedOffice.length === offices.length}
          onChange={(event) => handleSelectAll(event.target.checked)}
        />
      ),
      renderCell: ({ row }) => (
        <Checkbox
          checked={selectedOffice.includes(Number(row.id))}
          onChange={(event) => handleCheckboxChange(Number(row.id), event.target.checked)}
        />
      ),
    },
  ];

  const rows = offices.map((office: Office) => ({
    ...office,
    name: office.name,
  }));

  return (
    <HeaderResponsive>
      <StickyListToolbar>
        <Title>Asignar Usuarios a Oficinas</Title>
      </StickyListToolbar>
      <Card>
        <CardContent
          sx={{
            width: (theme) => stickyListRegionWidth(theme, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Seleccionar Oficinas
              </Typography>
            </Grid>
          </Grid>
          {showError && (
            <Grid container alignItems="center" justifyContent="center" sx={{ my: 1 }}>
              <Grid item xs={12} sm={8} md={5}>
                <Alert severity="error" onClick={() => setShowError(false)}>
                  {error}
                </Alert>
              </Grid>
            </Grid>
          )}
          {showSuccess && (
            <Grid container alignItems="center" justifyContent="center" sx={{ my: 1 }}>
              <Grid item xs={12} sm={8} md={5}>
                <Alert severity="success" onClick={() => setShowSuccess(false)}>
                  {success}
                </Alert>
              </Grid>
            </Grid>
          )}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box sx={{ width: '100%' }}>
                {offices.length > 0 ? (
                  <SimpleCustomTable
                    rows={rows}
                    columns={columns}
                    maxHeight={400}
                    showBorders
                    wrapCellContent
                  />
                ) : null}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
        <Divider sx={{ margin: 0 }} />
        <CardActions sx={{ px: 2, pb: 2 }}>
          <Button size="small" type="submit" sx={{ mr: 2 }} variant="contained" onClick={submit}>
            Guardar
          </Button>
          <Link to="/administrar/usuarios">
            <Button size="small" color="secondary" variant="outlined">
              Cancelar
            </Button>
          </Link>
        </CardActions>
      </Card>
    </HeaderResponsive>
  );
};

export default UserOffice;
